import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Dialog } from '~/client/src/shared/components/Dialog/Dialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description: React.ReactNode
  /** Any of these values typed exactly will enable confirmation */
  confirmationValues: string[]
  confirmButtonLabel: string
  loading?: boolean
}

let instanceCounter = 0

export default function TypeToConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmationValues,
  confirmButtonLabel,
  loading = false,
}: IProps) {
  const inputId = useMemo(() => `type-to-confirm-${++instanceCounter}`, [])
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) {
      setInputValue('')
    }
  }, [isOpen])

  const isConfirmEnabled = confirmationValues.some(
    v => v.toLowerCase() === inputValue.toLowerCase(),
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && isConfirmEnabled && !loading) {
        onConfirm()
      }
    },
    [isConfirmEnabled, loading, onConfirm],
  )

  return (
    <Dialog.Root
      open={isOpen}
      onClose={onClose}
      dismissible={!loading}
      initialFocus={inputRef}
    >
      <Dialog.Title>{title}</Dialog.Title>

      <Dialog.Body>
        <Dialog.Description className="tw:mb-4">
          {description}
        </Dialog.Description>

        <div className="tw:flex tw:flex-col tw:gap-1">
          <label
            htmlFor={inputId}
            className="tw:text-label-sm tw:text-md3-on-surface-variant"
          >
            {Localization.translator.typeToConfirm}
          </label>
          <input
            id={inputId}
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            ref={inputRef}
            className="tw:w-full tw:px-3 tw:py-2 tw:rounded-md tw:border tw:border-solid tw:border-md3-outline tw:bg-md3-surface tw:text-body-md tw:text-md3-on-surface tw:outline-none focus:tw:border-md3-primary focus:tw:ring-1 focus:tw:ring-md3-primary disabled:tw:opacity-50"
          />
        </div>
      </Dialog.Body>

      <Dialog.Actions>
        <Button
          variant="outline"
          size="default"
          onClick={onClose}
          disabled={loading}
        >
          {Localization.translator.cancel}
        </Button>
        <Button
          variant="destructive"
          size="default"
          onClick={onConfirm}
          disabled={!isConfirmEnabled || loading}
        >
          {confirmButtonLabel}
        </Button>
      </Dialog.Actions>
    </Dialog.Root>
  )
}
